import moment from "moment";

import TrainingAttendanceListForApprove from "issara-sdk/apis/TrainingAttendanceListForApprove_apps_HRM";
import TrainingAttendanceRetrieveUpdate from "issara-sdk/apis/TrainingAttendanceRetrieveUpdate_apps_HRM";

import TrainingAttendanceSerializer from "issara-sdk/types/TrainingAttendanceSerializer_apps_HRM";

import {
  ActionHandler,
  BaseSeqState,
  BLC_STATUS,
  GenerateActionTypes,
  generatePrefixedActions,
  PickMainState,
  SetErrorMessage,
} from "react-lib/apps/HISV3/common/CommonInterface";

import { formatDate } from "react-lib/utils/dateUtils";

/** ============== Type-Defining Constants =============  */
export const ACTIONS = { SEARCH: "SEARCH", UPDATE_STATUS: "UPDATE_STATUS" } as const;

export const SEQUENCE_NAME = "TrainingManagement" as const;

/** =============== Types and Interfaces ===============  */
export type State = Partial<{
  TrainingManagementSequence: Partial<{
    sequenceIndex: "Action" | "START" | null;
    filter: TrainingAttendanceFilterType;
    pagination: { activePage: number; items: TrainingAttendanceType[]; total: number };
  }> | null;
}>;

export type PickedState = PickMainState<
  "buttonLoadCheck" | "errorMessage" | "searchedItemListWithKey" | "selectedDivision"
>;

export type TrainingAttendanceType = TrainingAttendanceSerializer & {
  course_end_date: string;
  course_start_date: string;
};

export type TrainingAttendanceFilterType = Partial<{
  course_id: number | string;
  course_start_date_from: string;
  course_start_date_to: string;
  division_id: number;
  employee_id: number | null;
  employee_name: string;
  status: string;
  training_type: number;
}>;

export type UpdateStatusParamsType = { id?: number; status: TrainingAttendanceType["status"] };

export type Event =
  | { message: "GetMasterData"; params: Record<string, unknown> }
  | { message: "RunSequence"; params: Record<string, unknown> };

export type Data = {
  device?: number;
  division?: number;
};

export type SeqState = BaseSeqState<SequenceName> & { filter?: TrainingAttendanceFilterType };

export type Actions = typeof ACTIONS;

type SequenceName = typeof SEQUENCE_NAME;

type ActionPayloadMap = {
  [ACTIONS.SEARCH]: { activePage?: number };
  [ACTIONS.UPDATE_STATUS]: UpdateStatusParamsType;
};

type ActionTypes = GenerateActionTypes<
  Actions,
  ActionPayloadMap,
  SequenceName,
  PickedState & State,
  SeqState
>;

type ActionType = ActionTypes["ActionType"];

export type RunSequence = ActionTypes["RunSequence"];

export type SetProp = ActionTypes["SetProp"];

export type OnEvent = ActionTypes["OnEvent"];

type Params<A extends ActionType["action"]> = ActionTypes["Params"][A];

export type Handler<P = unknown, R = void> = ActionHandler<PickedState & State, Event, Data, P, R>;

/** ===================== Constants ====================  */
export const StateInitial: State = {
  TrainingManagementSequence: null,
};

export const DataInitial: Data = {};

export const LIST_LIMIT = 100;

export const BTN_ACTS = generatePrefixedActions(ACTIONS, SEQUENCE_NAME);

/** ===================== Functions ====================  */
export const Start: Handler<SeqState> = async (controller, params) => {
  const state = controller.getState();

  const now = moment();

  controller.setState(
    {
      TrainingManagementSequence: {
        ...state.TrainingManagementSequence,
        sequenceIndex: "Action",
        filter: {
          course_start_date_from: formatDate(now.startOf("months")),
          course_start_date_to: formatDate(now.endOf("months")),
          division_id: state.selectedDivision?.id,
          ...params.filter,
        },
      },
    },
    () => {
      Action(controller, { ...params, action: ACTIONS.SEARCH });
    }
  );
};

/** ====================================================  */
/**                        Action                         */
/** ====================================================  */
export const Action: Handler<ActionType> = async (controller, params) => {
  const actionHandlers: Partial<{ [K in ActionType["action"]]: Handler<Params<K>> }> = {
    [ACTIONS.SEARCH]: HandleSearch,
    [ACTIONS.UPDATE_STATUS]: HandleUpdateStatus,
  };

  const { action } = params;

  const handler = actionHandlers[action] as Handler<Params<typeof action>> | undefined;

  if (handler) {
    handler(controller, params);
  } else {
    console.error(`No handler found for action: ${action}`);
  }
};

/** ====================================================  */
/**                        Handler                        */
/** ====================================================  */
const HandleSearch: Handler<Params<Actions["SEARCH"]>, Promise<void>> = async (
  controller,
  params
) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SEARCH]: BLC_STATUS.LOADING },
  });

  const filter = state.TrainingManagementSequence?.filter;
  const activePage =
    params.activePage ?? state.TrainingManagementSequence?.pagination?.activePage ?? 1;
  const offset = (activePage - 1) * LIST_LIMIT;

  const [result] = await TrainingAttendanceListForApprove.list({
    apiToken: controller.apiToken,
    params: { limit: LIST_LIMIT, offset, ...filter },
  });

  controller.setState({
    TrainingManagementSequence: {
      ...state.TrainingManagementSequence,
      pagination: { activePage, items: result?.items ?? [], total: result?.total ?? 0 },
    },
  });

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SEARCH]: BLC_STATUS.SUCCESS },
  });
};

const HandleUpdateStatus: Handler<Params<Actions["UPDATE_STATUS"]>> = async (
  controller,
  params
) => {
  const state = controller.getState();

  const btnKey = `${BTN_ACTS.UPDATE_STATUS}_${params.status}_${params.id}`;

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [btnKey]: BLC_STATUS.LOADING },
  });

  const [, error] = await TrainingAttendanceRetrieveUpdate.patch({
    apiToken: controller.apiToken,
    data: { status: params.status },
    pk: params.id,
  });

  if (error) {
    SetErrorMessage(controller, { ...params, action: btnKey, error });

    return;
  }

  const activePage = state.TrainingManagementSequence?.pagination?.activePage ?? 1;

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [btnKey]: null },
  });

  await HandleSearch(controller, { ...params, action: ACTIONS.SEARCH, activePage });
};
